import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { kbfAPI, errMsg, fmtDate, usd, safeHref } from '../services/intelligenceApi';
import './Intelligence.css';

/* INTELLIGENCE → KB REVIEW (brain v2 PLAN-CONCIERGE-FACTORY §5.6, §9.2).
 *
 * The KB factory's drafts wait here for a person. Mechanical drafts (fees, dates, URLs, quote sidecars) go to
 * ops; legal drafts (anything that changes what a rule means) go to Rishabh. The tier is recomputed by apbe
 * from the draft's diff and the API refuses the wrong role; this page only shows what the API allows. */

const TABS = ['review', 'tasks', 'jobs', 'sources', 'metrics'];
const STATUSES = ['in_review', 'verify_pending', 'revalidate', 'revise', 'approved', 'rejected', 'published', 'invalid', 'quote_failed', 'blocked_field'];

export function TierBadge({ tier }) {
  return <span className={`in-badge ${tier === 'legal' ? 'legal' : 'mechanical'}`}>{tier === 'legal' ? 'LEGAL' : 'MECHANICAL'}</span>;
}
export function VerdictBadge({ verdict }) {
  return <span className={`in-badge ${verdict || 'warn'}`}>{verdict || 'no verdict'}</span>;
}
export function RoleChips({ viewer }) {
  if (!viewer) return null;
  const roles = [viewer.ops && 'ops reviewer', viewer.legal && 'legal approver', viewer.legal_second && 'second legal reviewer',
    viewer.lang && 'bilingual reviewer', viewer.annotator && 'annotator', viewer.exporter && 'exporter'].filter(Boolean);
  return (
    <div className="in-row">
      <span className="in-muted">You:</span>
      {roles.length ? roles.map((r) => <span key={r} className="in-badge role">{r}</span>) : <span className="in-badge">read only</span>}
    </div>
  );
}

function ReviewTab({ viewer }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('in_review');
  const [tier, setTier] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await kbfAPI.drafts({ status, tier: tier || undefined, limit: 200 });
      setItems(r.data.items || []);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [status, tier]);
  useEffect(() => { load(); }, [load]);

  // your role first: a legal approver sees legal drafts first, an ops reviewer mechanical ones
  const sorted = useMemo(() => {
    const first = viewer?.legal ? 'legal' : viewer?.ops ? 'mechanical' : null;
    return [...items].sort((a, b) => (Number(b.can_decide) - Number(a.can_decide)) || ((b.tier === first) - (a.tier === first))
      || (Number(b.red) - Number(a.red)) || (a.id - b.id));
  }, [items, viewer]);

  return (
    <div className="in-card">
      <div className="in-row" style={{ marginBottom: 10 }}>
        <select className="in-select" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select className="in-select" value={tier} onChange={(e) => setTier(e.target.value)} aria-label="Tier">
          <option value="">all tiers</option><option value="mechanical">mechanical</option><option value="legal">legal</option>
        </select>
        <span className="in-spacer" />
        <button type="button" className="in-btn small" onClick={load}>Refresh</button>
      </div>
      {error && <div className="in-banner bad">{error}</div>}
      {loading ? <div className="in-empty">Loading drafts…</div> : !sorted.length ? <div className="in-empty">Nothing here.</div> : (
        <div className="in-table-wrap">
          <table className="in-table">
            <thead>
              <tr><th>#</th><th>Record</th><th>Change</th><th>Tier</th><th>Verifier</th><th>Why legal</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {sorted.map((d) => (
                <tr key={d.id} className="clickable" onClick={() => navigate(`/intelligence/kb/drafts/${d.id}`)}>
                  <td className="in-mono">{d.id}</td>
                  <td><div className="in-mono">{d.record_id}</div><div className="in-muted">{d.job_id} · rev {d.revision}</div></td>
                  <td>{d.action} → {d.target}{d.upgrade && <div><span className="in-badge warn">upgrade</span></div>}</td>
                  <td>
                    <TierBadge tier={d.tier} />
                    {d.tier_mismatch && <div className="in-muted" title="stored tier differs from the recomputed one">stored: {d.stored_tier}</div>}
                  </td>
                  <td><VerdictBadge verdict={d.verdict} />{d.diff_count > 0 && <div className="in-muted">{d.diff_count} field(s) differ</div>}</td>
                  <td className="in-muted">{(d.tier_reasons || []).slice(0, 3).join('; ') || '—'}{d.non_english && <div>non-English source</div>}</td>
                  <td><span className="in-badge">{d.status}</span></td>
                  <td>{d.can_decide ? <span className="in-badge ok">yours</span> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TasksTab() {
  const [status, setStatus] = useState('open');
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    try {
      setItems((await kbfAPI.tasks({ status })).data.items || []);
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [status]);
  useEffect(() => { load(); }, [load]);
  const resolve = async (t, next) => {
    const note = window.prompt(`${next === 'open' ? 'Reopen' : next === 'done' ? 'Mark done' : 'Dismiss'} task ${t.id}: note (optional)`) ?? null;
    if (note === null) return;
    setBusy(t.id);
    try {
      await kbfAPI.updateTask(t.id, { status: next, note });
      await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="in-card">
      <div className="in-row" style={{ marginBottom: 10 }}>
        <select className="in-select" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Task status">
          <option value="open">open</option><option value="done">done</option><option value="dismissed">dismissed</option><option value="all">all</option>
        </select>
      </div>
      {error && <div className="in-banner bad">{error}</div>}
      {!items.length ? <div className="in-empty">No tasks.</div> : (
        <div className="in-table-wrap">
          <table className="in-table">
            <thead><tr><th>#</th><th>Kind</th><th>Severity</th><th>Records</th><th>Detail</th><th>Created</th><th /></tr></thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id}>
                  <td className="in-mono">{t.id}</td>
                  <td>{t.kind}</td>
                  <td><span className={`in-badge ${t.severity === 'high' ? 'bad' : ''}`}>{t.severity}</span></td>
                  <td className="in-mono">{(t.record_ids || []).slice(0, 4).join(', ')}</td>
                  <td className="in-muted" style={{ maxWidth: 380 }}>{summarizeDetail(t)}</td>
                  <td className="in-muted">{fmtDate(t.created_at)}</td>
                  <td>
                    {t.kind === 'second_check' && t.detail?.draft ? (
                      <Link className="in-btn small" to={`/intelligence/kb/drafts/${t.detail.draft}`}>Open draft</Link>
                    ) : t.status === 'open' ? (
                      <div className="in-row">
                        <button type="button" className="in-btn small" disabled={busy === t.id} onClick={() => resolve(t, 'done')}>Done</button>
                        <button type="button" className="in-btn small" disabled={busy === t.id} onClick={() => resolve(t, 'dismissed')}>Dismiss</button>
                      </div>
                    ) : (
                      <button type="button" className="in-btn small" disabled={busy === t.id} onClick={() => resolve(t, 'open')}>Reopen</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function summarizeDetail(t) {
  const d = t.detail || {};
  if (t.kind === 'second_check') return `draft ${d.draft} · ${d.tier} · ${d.signoff || ''}${d.fast ? ' · fast approval' : ''} → ${d.to || ''}`;
  if (t.kind === 'kb_gap') return `from a concierge annotation: ${d.note || ''}`;
  if (t.kind === 'manual_upload') return `${d.url} (${d.content_type}, ${d.bytes} bytes) — run the factory's upload`;
  if (d.why) return d.why;
  if (d.note) return d.note;
  if (d.error) return d.error;
  return JSON.stringify(d).slice(0, 200);
}

function JobsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ kind: 'quote', jurisdiction: '', topic: '', question: '', priority: 0 });
  const [msg, setMsg] = useState(null);
  const load = useCallback(async () => {
    try {
      setItems((await kbfAPI.jobs({})).data.items || []);
    } catch (e) {
      setMsg({ bad: true, text: errMsg(e) });
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await kbfAPI.createJob({ ...form, priority: Number(form.priority) || 0 });
      setMsg({ text: `Queued ${r.data.job.id}. The factory picks it up on its next tick.` });
      setForm({ kind: 'quote', jurisdiction: '', topic: '', question: '', priority: 0 });
      await load();
    } catch (err) {
      setMsg({ bad: true, text: errMsg(err) });
    }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className="in-grid2">
      <div className="in-card">
        <h3>Jobs</h3>
        {!items.length ? <div className="in-empty">No jobs.</div> : (
          <div className="in-table-wrap">
            <table className="in-table">
              <thead><tr><th>Job</th><th>Kind</th><th>J.</th><th>Topic</th><th>Status</th><th>Cost</th></tr></thead>
              <tbody>
                {items.map((j) => (
                  <tr key={j.id}>
                    <td className="in-mono">{j.id}<div className="in-muted">p{j.priority} · {j.created_by}</div></td>
                    <td>{j.kind}</td><td>{j.jurisdiction || '—'}</td>
                    <td className="in-mono">{j.topic}{j.error && <div className="in-muted">{String(j.error).slice(0, 160)}</div>}</td>
                    <td><span className="in-badge">{j.status}</span></td>
                    <td>{usd(j.cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <form className="in-card" onSubmit={submit}>
        <h3>New job</h3>
        {msg && <div className={`in-banner ${msg.bad ? 'bad' : 'info'}`}>{msg.text}</div>}
        <label className="in-field">Kind
          <select className="in-select" value={form.kind} onChange={set('kind')}>
            <option value="quote">quote (find the official quote for a record)</option>
            <option value="quote_upgrade">quote_upgrade (quote + propose high confidence)</option>
            <option value="create">create (a new record)</option>
          </select>
        </label>
        <label className="in-field">Jurisdiction (ISO)<input className="in-input" value={form.jurisdiction} onChange={set('jurisdiction')} placeholder="AE" /></label>
        <label className="in-field">{form.kind === 'create' ? 'Topic' : 'Record id'}<input className="in-input" value={form.topic} onChange={set('topic')} placeholder="AE.TAX.PIT.NONE" /></label>
        <label className="in-field">Question (no figures: the blind verifier must not be anchored on numbers)
          <textarea className="in-textarea" value={form.question} onChange={set('question')} />
        </label>
        <label className="in-field">Priority (-100..100)<input className="in-input" type="number" value={form.priority} onChange={set('priority')} /></label>
        <button type="submit" className="in-btn primary">Queue job</button>
      </form>
    </div>
  );
}

function SourcesTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ source_id: '', url: '', class: 'tax_authority', title: '', topics: '' });
  const [upload, setUpload] = useState({ url: '', source_id: '', file: null, attest: false });
  const [msg, setMsg] = useState(null);
  const load = useCallback(async () => {
    try {
      setItems((await kbfAPI.sources()).data.items || []);
    } catch (e) {
      setMsg({ bad: true, text: errMsg(e) });
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const propose = async (e) => {
    e.preventDefault();
    try {
      await kbfAPI.createSource({ ...form, topics: form.topics.split(',').map((x) => x.trim()).filter(Boolean) });
      setMsg({ text: `Proposed ${form.source_id.toUpperCase()}. It is fetched only after its host and path are added to the registry allowlist (R-S8).` });
      setForm({ source_id: '', url: '', class: 'tax_authority', title: '', topics: '' });
      await load();
    } catch (err) {
      setMsg({ bad: true, text: errMsg(err) });
    }
  };
  const sendUpload = async (e) => {
    e.preventDefault();
    if (!upload.file) return;
    if (upload.file.size > 10 * 1024 * 1024) { setMsg({ bad: true, text: 'File over 10 MB' }); return; }
    const buf = new Uint8Array(await upload.file.arrayBuffer());
    let bin = '';
    for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode.apply(null, buf.subarray(i, i + 0x8000));
    try {
      const r = await kbfAPI.upload({ url: upload.url, source_id: upload.source_id || null, attest: upload.attest,
        content_type: upload.file.type || (upload.file.name.endsWith('.pdf') ? 'application/pdf' : 'text/html'), filename: upload.file.name, data_base64: btoa(bin) });
      setMsg({ text: `Uploaded (task ${r.data.task_id}, sha256 ${r.data.sha256.slice(0, 12)}…). The factory ingests it as a snapshot.` });
      setUpload({ url: '', source_id: '', file: null, attest: false });
    } catch (err) {
      setMsg({ bad: true, text: errMsg(err) });
    }
  };
  return (
    <>
      {msg && <div className={`in-banner ${msg.bad ? 'bad' : 'info'}`}>{msg.text}</div>}
      <div className="in-card">
        <h3>Sources</h3>
        {!items.length ? <div className="in-empty">No sources mirrored yet.</div> : (
          <div className="in-table-wrap">
            <table className="in-table">
              <thead><tr><th>Source</th><th>Class</th><th>Title</th><th>Fetch</th><th>Status</th><th>Last fetched</th></tr></thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.source_id}>
                    <td className="in-mono">{s.source_id}</td><td>{s.class}</td>
                    <td>{safeHref(s.url) ? <a href={safeHref(s.url)} target="_blank" rel="noopener noreferrer nofollow">{s.title}</a> : s.title}</td>
                    <td>{s.fetch_mode} · {s.watch}</td>
                    <td><span className={`in-badge ${s.status === 'active' ? 'ok' : 'warn'}`}>{s.status}</span></td>
                    <td className="in-muted">{fmtDate(s.last_fetched_at)}{s.last_http_status ? ` · ${s.last_http_status}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="in-grid2">
        <form className="in-card" onSubmit={propose}>
          <h3>Propose a source</h3>
          <label className="in-field">Source id<input className="in-input" value={form.source_id} onChange={set('source_id')} placeholder="AE.MOF_FAQ" /></label>
          <label className="in-field">URL (https)<input className="in-input" value={form.url} onChange={set('url')} /></label>
          <label className="in-field">Class
            <select className="in-select" value={form.class} onChange={set('class')}>
              {['legislation', 'tax_authority', 'regulator', 'government', 'court', 'other'].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="in-field">Title<input className="in-input" value={form.title} onChange={set('title')} /></label>
          <label className="in-field">Topics (comma separated)<input className="in-input" value={form.topics} onChange={set('topics')} /></label>
          <button type="submit" className="in-btn primary">Propose</button>
        </form>
        <form className="in-card" onSubmit={sendUpload}>
          <h3>Upload a saved page or document</h3>
          <p className="in-muted">For a source the factory cannot fetch. Save the page (or PDF) from your browser and attest its URL.</p>
          <label className="in-field">URL it came from<input className="in-input" value={upload.url} onChange={(e) => setUpload((u) => ({ ...u, url: e.target.value }))} /></label>
          <label className="in-field">Source id (optional)<input className="in-input" value={upload.source_id} onChange={(e) => setUpload((u) => ({ ...u, source_id: e.target.value }))} /></label>
          <label className="in-field">File (HTML, PDF or text, ≤ 10 MB)
            <input type="file" accept=".html,.htm,.xhtml,.pdf,.txt,text/html,application/pdf,text/plain" onChange={(e) => setUpload((u) => ({ ...u, file: e.target.files[0] || null }))} />
          </label>
          <label className="in-row in-muted"><input type="checkbox" checked={upload.attest} onChange={(e) => setUpload((u) => ({ ...u, attest: e.target.checked }))} />
            I attest this file is the page at that URL, as retrieved today.</label>
          <button type="submit" className="in-btn primary" disabled={!upload.file || !upload.attest}>Upload</button>
        </form>
      </div>
    </>
  );
}

function MetricsTab({ summary }) {
  if (!summary) return null;
  const s = summary.spend;
  const pct = s.openai_pct_of_limit || 0;
  return (
    <div className="in-grid3">
      <div className="in-card">
        <h3>Queue</h3>
        <div className="in-kpi"><b>{summary.queue.in_review} / {summary.queue.cap}</b><span className="in-muted">in review</span></div>
        <p className="in-muted">mechanical {summary.queue.by_tier.mechanical} · legal {summary.queue.by_tier.legal} · disagreed {summary.queue.disagreed}
          · waiting for the verifier {summary.queue.verify_pending} · second checks open {summary.second_checks_open}</p>
      </div>
      <div className="in-card">
        <h3>OpenAI (verifier) vs hard limit</h3>
        <div className="in-kpi"><b>{usd(s.month.openai)}</b><span className="in-muted">this month of {usd(s.openai_hard_limit_usd)} ({pct}%)</span></div>
        <div className={`in-bar ${pct >= 80 ? 'hot' : ''}`} style={{ marginTop: 8 }}><span style={{ width: `${Math.min(100, pct)}%` }} /></div>
        <p className="in-muted">today {usd(s.today.openai)} (cap {usd(s.daily_caps_usd.openai)}/day)</p>
      </div>
      <div className="in-card">
        <h3>Bedrock (drafter)</h3>
        <div className="in-kpi"><b>{usd(s.month.bedrock_claude)}</b><span className="in-muted">this month</span></div>
        <p className="in-muted">today {usd(s.today.bedrock_claude)} (cap {usd(s.daily_caps_usd.bedrock_claude)}/day)</p>
      </div>
      <div className="in-card">
        <h3>Drafts by status</h3>
        <table className="in-table"><tbody>
          {summary.drafts.map((r) => <tr key={`${r.status}${r.tier}${r.verdict}`}><td>{r.status}</td><td>{r.tier}</td><td>{r.verdict || '—'}</td><td>{r.n}</td></tr>)}
        </tbody></table>
      </div>
      <div className="in-card">
        <h3>Jobs by status</h3>
        <table className="in-table"><tbody>{summary.jobs.map((r) => <tr key={r.status}><td>{r.status}</td><td>{r.n}</td></tr>)}</tbody></table>
      </div>
      <div className="in-card">
        <h3>Open tasks</h3>
        <table className="in-table"><tbody>{summary.tasks.map((r) => <tr key={`${r.kind}${r.severity}`}><td>{r.kind}</td><td>{r.severity}</td><td>{r.n}</td></tr>)}</tbody></table>
      </div>
    </div>
  );
}

export default function IntelligenceKb() {
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return TABS.includes(t) ? t : 'review';
  });
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    kbfAPI.summary().then((r) => setSummary(r.data)).catch((e) => setError(errMsg(e)));
  }, [tab]);
  const go = (t) => {
    setTab(t);
    window.history.replaceState(null, '', `?tab=${t}`);
  };
  const c = summary?.config;
  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">KB REVIEW</h1>
            <p className="page-subtitle">Rule drafts from the KB factory: Claude drafts, an OpenAI verifier extracts the same rule blind, a person signs.</p>
          </div>
          <RoleChips viewer={summary?.viewer} />
        </div>
        {error && <div className="in-banner bad">{error}</div>}
        {summary?.queue.full && <div className="in-banner warn">The review queue is at {summary.queue.cap}: the factory has paused new drafts until some are decided.</div>}
        {c && (!c.ops_reviewers || !c.legal_approvers) && <div className="in-banner warn">No {!c.ops_reviewers ? 'ops reviewers (KBF_OPS_REVIEWERS)' : 'legal approvers (KBF_LEGAL_APPROVERS)'} are configured in apbe: those drafts cannot be decided.</div>}
        {c && !c.legal_second_named && summary?.second_checks_open > 0 && <div className="in-banner info">KBF_LEGAL_SECOND is not named yet: legal second checks go to another legal approver.</div>}
        <div className="in-tabs" role="tablist">
          {TABS.map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t} className={`in-tab ${tab === t ? 'active' : ''}`} onClick={() => go(t)}>
              {t.toUpperCase()}
              {t === 'review' && summary && <span className="in-badge in-count">{summary.queue.in_review}</span>}
              {t === 'tasks' && summary && <span className="in-badge in-count">{summary.tasks.reduce((n, r) => n + r.n, 0)}</span>}
            </button>
          ))}
        </div>
        {tab === 'review' && <ReviewTab viewer={summary?.viewer} />}
        {tab === 'tasks' && <TasksTab />}
        {tab === 'jobs' && <JobsTab />}
        {tab === 'sources' && <SourcesTab />}
        {tab === 'metrics' && <MetricsTab summary={summary} />}
      </main>
    </div>
  );
}
